import {
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import axios from 'axios'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ActivityModel } from 'types'
import { toast } from 'react-hot-toast'

// Define a type for the creation payload, which omits the 'id'
type ActivityCreationPayload = Omit<ActivityModel, 'id'>

const initialActivity: ActivityCreationPayload = {
  title: '',
  instructions: '',
  instructionsCloseCircle: '',
  instructionsOther: '',
  support: 'none',
  category: 'ludique',
  baseDifficulty: 3,
  basePleasure: 3,
}

export default function ActivityCreationPage() {
  const navigate = useNavigate()
  const [activity, setActivity] =
    useState<ActivityCreationPayload>(initialActivity)
  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = () => {
    if (!activity.title || !activity.instructions) {
      toast.error('Le titre et les instructions sont obligatoires.')
      return
    }

    setIsSaving(true)

    // Prépare le payload en s'assurant que les champs optionnels vides sont envoyés comme null
    const payload = {
      ...activity,
      instructionsCloseCircle: activity.instructionsCloseCircle || null,
      instructionsOther: activity.instructionsOther || null,
    }

    axios
      .post(base_url + '/activity/', payload)
      .then((response) => {
        toast.success('Activité créée avec succès !')
        navigate('/activities')
      })
      .catch((error) => {
        console.error('Error creating activity:', error)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <PageAppBar pageName="Créer une Activité">
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '700px',
          margin: 'auto',
        }}
        noValidate
        autoComplete="off"
      >
        <Typography variant="h5">Nouvelle Activité</Typography>
        <TextField
          required
          fullWidth
          label="Titre"
          value={activity.title}
          variant="outlined"
          onChange={(e) => setActivity({ ...activity, title: e.target.value })}
          disabled={isSaving}
        />
        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Instructions (Parents)"
          value={activity.instructions}
          variant="outlined"
          onChange={(e) =>
            setActivity({ ...activity, instructions: e.target.value })
          }
          disabled={isSaving}
        />
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Instructions pour le cercle proche (Optionnel)"
          value={activity.instructionsCloseCircle || ''}
          variant="outlined"
          onChange={(e) =>
            setActivity({
              ...activity,
              instructionsCloseCircle: e.target.value,
            })
          }
          disabled={isSaving}
        />
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Instructions pour une autre personne (Optionnel)"
          value={activity.instructionsOther || ''}
          variant="outlined"
          onChange={(e) =>
            setActivity({ ...activity, instructionsOther: e.target.value })
          }
          disabled={isSaving}
        />
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="support-label">Support de lecture</InputLabel>
          <Select
            labelId="support-label"
            value={activity.support}
            label="Support de lecture"
            onChange={(e) =>
              setActivity({
                ...activity,
                support: e.target.value as ActivityModel['support'],
              })
            }
          >
            <MenuItem value="none">Aucun</MenuItem>
            <MenuItem value="comic_book">Bande dessinée</MenuItem>
            <MenuItem value="picture_book">Album illustré</MenuItem>
            <MenuItem value="youth_novel">Roman jeunesse</MenuItem>
            <MenuItem value="youth_magazine">Magazine jeunesse</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="category-label">Catégorie</InputLabel>
          <Select
            labelId="category-label"
            value={activity.category}
            label="Catégorie"
            onChange={(e) =>
              setActivity({
                ...activity,
                category: e.target.value as ActivityModel['category'],
              })
            }
          >
            <MenuItem value="ludique">Ludique</MenuItem>
            <MenuItem value="auto">Autonomie</MenuItem>
            <MenuItem value="renf">Renforcement</MenuItem>
            <MenuItem value="partag">Partage</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="difficulty-label">Difficulté de base</InputLabel>
          <Select
            labelId="difficulty-label"
            value={activity.baseDifficulty}
            label="Difficulté de base"
            onChange={(e) =>
              setActivity({
                ...activity,
                baseDifficulty: Number(
                  e.target.value
                ) as ActivityModel['baseDifficulty'],
              })
            }
          >
            {[1, 2, 3, 4, 5].map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="pleasure-label">Plaisir de base</InputLabel>
          <Select
            labelId="pleasure-label"
            value={activity.basePleasure}
            label="Plaisir de base"
            onChange={(e) =>
              setActivity({
                ...activity,
                basePleasure: Number(
                  e.target.value
                ) as ActivityModel['basePleasure'],
              })
            }
          >
            {[1, 2, 3, 4, 5].map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ mt: 2, minWidth: '150px' }}
        >
          {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Créer'}
        </Button>
      </Box>
    </PageAppBar>
  )
}
